import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useParams, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import store from './store/store';
import { PermissionProvider } from './contexts/PermissionContext';

// Layout
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Header from './components/Layout/Header';
import MobileBottomNav from './components/MobileBottomNav';

// ── Eager (critical path — needed on first render) ──
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';
import WhatsAppButton from './components/Common/WhatsAppButton';
import AppUpdateBanner from './components/Common/AppUpdateBanner';

// ── Lazy-loaded (split into separate chunks) ──
const MakesPage = lazy(() => import('./pages/MakesPage'));
const EnhancedModelsPage = lazy(() => import('./pages/EnhancedModelsPage'));
const BodyTypesRouter = lazy(() => import('./pages/BodyTypesRouter'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Register1 = lazy(() => import('./pages/Register1'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage'));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage'));
const AccessoriesSupplies = lazy(() => import('./pages/AccessoriesSupplies'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AutoglassVinSearch = lazy(() => import('./pages/AutoglassVinSearch'));
const PaymentTestPage = lazy(() => import('./pages/PaymentTestPage'));
const WishPage = lazy(() => import('./pages/WishPage'));
const WatchlistPage = lazy(() => import('./pages/WatchlistPage'));
const QuoteRequestsPage = lazy(() => import('./pages/QuoteRequestsPage'));
const PageRenderer = lazy(() => import('./pages/PageRenderer'));
const CatalogueSearchPage = lazy(() => import('./pages/CatalogueSearchPage'));
const PartsFinderPage = lazy(() => import('./pages/PartsFinderPage'));
const QuoteResponse = lazy(() => import('./pages/QuoteResponse'));
const VinSearch = lazy(() => import('./pages/VinSearch'));

// SEO landing pages — lazy
const WindscreenReplacementSydney = lazy(() => import('./pages/seo/WindscreenReplacementSydney'));
const CarGlassRepairSydney = lazy(() => import('./pages/seo/CarGlassRepairSydney'));
const AutoGlassServicesNSW = lazy(() => import('./pages/seo/AutoGlassServicesNSW'));
const WindscreenReplacementMelbourne = lazy(() => import('./pages/seo/WindscreenReplacementMelbourne'));
const AutoGlassServicesVIC = lazy(() => import('./pages/seo/AutoGlassServicesVIC'));

// Mobile pages — lazy
const MobileHomePage = lazy(() => import('./pages/mobile/MobileHomePage'));
const MobileMakesPage = lazy(() => import('./pages/mobile/MobileMakesPage'));
const MobileModelsPage = lazy(() => import('./pages/mobile/MobileModelsPage'));
const MobileBodyTypesPage = lazy(() => import('./pages/mobile/MobileBodyTypesPage'));
const MobileCartPage = lazy(() => import('./pages/mobile/MobileCartPage'));
const MobileCheckoutPage = lazy(() => import('./pages/mobile/MobileCheckoutPage'));
const MobileAccessoriesPage = lazy(() => import('./pages/mobile/MobileAccessoriesPage'));
const MobilePartsFinderPage = lazy(() => import('./pages/mobile/MobilePartsFinderPage'));
const MobileAccountsPage = lazy(() => import('./pages/mobile/MobileAccountsPage'));
const MobileWatchlistPage = lazy(() => import('./pages/mobile/MobileWatchlistPage'));
const MobileProductDetailPage = lazy(() => import('./pages/mobile/MobileProductDetailPage'));
const MobileContactPage = lazy(() => import('./pages/mobile/MobileContactPage'));
const MobileVinSearchPage = lazy(() => import('./pages/mobile/MobileVinSearchPage'));
const MobilePositionsPage = lazy(() => import('./pages/mobile/MobilePositionsPage'));
const MobileProductsPage = lazy(() => import('./pages/mobile/MobileProductsPage'));
const MobileYearPage = lazy(() => import('./pages/mobile/MobileYearPage'));
const MobileLoginPage = lazy(() => import('./pages/mobile/MobileLoginPage'));
const MobileSearchResultsPage = lazy(() => import('./pages/mobile/MobileSearchResultsPage'));
const MobileQuoteRequestPage = lazy(() => import('./pages/mobile/MobileQuoteRequestPage'));
const MobilePageRenderer = lazy(() => import('./pages/mobile/MobilePageRenderer'));
const QuickEntryPage = lazy(() => import('./pages/QuickEntryPage'));
const MobileQuickEntryPage = lazy(() => import('./pages/mobile/MobileQuickEntryPage'));

// Admin pages — lazy (only admins will load these)
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/AdminProducts'));
const AdminCategories = lazy(() => import('./pages/AdminCategories'));
const AdminCSVUpload = lazy(() => import('./pages/AdminCSVUpload'));
const AdminCsvExport = lazy(() => import('./pages/AdminCsvExport'));
const AdminProductAttributes = lazy(() => import('./pages/AdminProductAttributes'));
const AdminProductForm = lazy(() => import('./pages/AdminProductForm'));
const AdminMakes = lazy(() => import('./pages/AdminMakes'));
const AdminModels = lazy(() => import('./pages/AdminModels'));
const AdminBodyTypes = lazy(() => import('./pages/AdminBodyTypes'));
const AdminPositions = lazy(() => import('./pages/AdminPositions'));
const AdminWarehouses = lazy(() => import('./pages/AdminWarehouses'));
const AdminStockLevels = lazy(() => import('./pages/AdminStockLevels'));
const AdminRealtimeStock = lazy(() => import('./pages/AdminRealtimeStock'));
const AdminCustomers = lazy(() => import('./pages/AdminCustomers'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminCustomPricing = lazy(() => import('./pages/AdminCustomPricing'));
const AdminCustomerPrices = lazy(() => import('./pages/AdminCustomerPrices'));
const AdminCustomerDetail = lazy(() => import('./pages/AdminCustomerDetail'));
const AdminProductPricing = lazy(() => import('./pages/AdminProductPricing'));
const AdminOrders = lazy(() => import('./pages/AdminOrders'));
const AdminLowStock = lazy(() => import('./pages/AdminLowStock'));
const AdminDiagramMapper = lazy(() => import('./pages/AdminDiagramMapper'));
const AdminVinDecode = lazy(() => import('./pages/AdminVinDecode'));
const AdminUOM = lazy(() => import('./pages/AdminUOM'));
const AdminCompetitors = lazy(() => import('./pages/AdminCompetitors'));
const AdminPriceCategories = lazy(() => import('./pages/AdminPriceCategories'));
const AdminRolePermissions = lazy(() => import('./pages/AdminRolePermissions'));
const AdminPendingUsers = lazy(() => import('./pages/AdminPendingUsers'));
const AdminProductAccessories = lazy(() => import('./pages/AdminProductAccessories'));
const AdminCommonUse = lazy(() => import('./pages/AdminCommonUse'));
const AdminQuoteManagement = lazy(() => import('./pages/AdminQuoteManagement'));
const AdminVinDecodeCsvUpload = lazy(() => import('./pages/admin/AdminVinDecodeCsvUpload'));
const AdminVinDecodeCsvExport = lazy(() => import('./pages/admin/AdminVinDecodeCsvExport'));
const AdminThemeSettings = lazy(() => import('./pages/AdminThemeSettings'));
const AdminPricingSettings = lazy(() => import('./pages/AdminPricingSettings'));
const AdminWarehouseEmailSettings = lazy(() => import('./pages/AdminWarehouseEmailSettings'));
const BatchVinTester = lazy(() => import('./pages/admin/BatchVinTester'));
const AdminProductImages = lazy(() => import('./pages/AdminProductImages'));
const AdminProductDuplicates = lazy(() => import('./pages/AdminProductDuplicates'));
const AdminVehicleFitments = lazy(() => import('./pages/AdminVehicleFitments'));
const AdminCMSPages = lazy(() => import('./pages/AdminCMSPages'));
const AdminCSVCombiner = lazy(() => import('./pages/admin/AdminCSVCombiner'));
const AdminCompetitorCodesReview = lazy(() => import('./pages/admin/AdminCompetitorCodesReview'));
const AdminIdentifyNewVehicle = lazy(() => import('./pages/AdminIdentifyNewVehicle'));
const AdminNewProductsDevelopment = lazy(() => import('./pages/AdminNewProductsDevelopment'));
const AdminCompositeCoverageGaps = lazy(() => import('./pages/AdminCompositeCoverageGaps'));
const AdminCoverageGaps = lazy(() => import('./pages/AdminCoverageGaps'));
const AdminMarketResearch = lazy(() => import('./pages/AdminMarketResearch'));
const AdminMynagsData = lazy(() => import('./pages/AdminMynagsData'));
const AdminMynagsResearch = lazy(() => import('./pages/AdminMynagsResearch'));
const AdminHiddenResearch = lazy(() => import('./pages/AdminHiddenResearch'));
const AdminAccessoryReview = lazy(() => import('./pages/AdminAccessoryReview'));
const AdminIPBlocklist = lazy(() => import('./pages/AdminIPBlocklist'));
const AdminIPBlocks = lazy(() => import('./pages/AdminIPBlocks'));

// Redirects /mobile/:makeSlug[/:modelSlug[/:productSlug]][?...] → desktop equivalent
const MobileSlugModelRedirect = () => {
  const { makeSlug, modelSlug, productSlug } = useParams();
  const location = useLocation();
  const path = [makeSlug, modelSlug, productSlug].filter(Boolean).join('/');
  return <Navigate to={`/${path}${location.search}`} replace />;
};

const RedirectToMobilePositions = () => {
  const { makeId, modelId, bodyTypeId } = useParams();
  return <Navigate to={`/mobile/makes/${makeId}/models/${modelId}/body-types/${bodyTypeId}/positions`} replace />;
};

const RedirectToMobilePositionsProducts = () => {
  const { makeId, modelId, bodyTypeId } = useParams();
  return <Navigate to={`/mobile/makes/${makeId}/models/${modelId}/body-types/${bodyTypeId}/positions/products`} replace />;
};


const DesktopRedirect = ({ children }) => {
  const location = useLocation();
  const [isDesktop, setIsDesktop] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return;
    const mediaQuery = window.matchMedia('(min-width: 769px)');
    const handleChange = (event) => setIsDesktop(event.matches);
    setIsDesktop(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy Safari
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  if (!isDesktop) return children;

  // Map mobile paths to desktop equivalents
  const mobilePath = location.pathname;
  const search = location.search;

  const stripMobilePrefix = (path) => path.replace(/^\/mobile/, '') || '/';

  // Routes that would redirect back to mobile or don't exist on desktop
  const redirectToHome = [
    /^\/mobile\/makes\/[^/]+\/models\/[^/]+\/body-types\/[^/]+\/positions$/,
    /^\/mobile\/makes\/[^/]+\/models\/[^/]+\/body-types\/[^/]+\/positions\/products$/,
    /^\/mobile\/makes\/[^/]+\/models\/[^/]+\/body-types$/,
    /^\/mobile\/makes\/[^/]+\/models\/[^/]+\/year$/,
    /^\/mobile\/makes\/[^/]+\/models\/[^/]+\/year\/[^/]+\/body-types$/,
    /^\/mobile\/makes\/[^/]+\/models\/[^/]+\/year\/[^/]+\/body-types\/[^/]+\/positions$/,
    /^\/mobile\/makes\/[^/]+\/models\/[^/]+\/year\/[^/]+\/body-types\/[^/]+\/positions\/products$/,
    /^\/mobile\/models$/,
    /^\/mobile\/products\/[^/]+\/request-quote$/,
    /^\/mobile\/search$/,
  ];

  if (redirectToHome.some((re) => re.test(mobilePath))) {
    return <Navigate to={'/' + search} replace />;
  }

  // Simple strip /mobile/ prefix for most routes
  let desktopPath = stripMobilePrefix(mobilePath);

  // Account path has a different name on desktop
  desktopPath = desktopPath.replace(/^\/accounts$/, '/account');
  desktopPath = desktopPath.replace(/^\/accessories$/, '/accessories-supplies');

  if (desktopPath !== mobilePath) {
    return <Navigate to={desktopPath + search} replace />;
  }

  return children;
};

const MobileShell = () => {
  const outletRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (outletRef.current) {
      outletRef.current.scrollTop = 0;
    }
  }, [pathname]);

  return (
    <DesktopRedirect>
      <div className="h-[100dvh] bg-slate-100 flex justify-center overflow-hidden">
        <div className="w-full max-w-sm h-full bg-slate-100 mx-auto overflow-hidden flex flex-col">
          <div className="flex-shrink-0">
            <Header />
          </div>
          <div ref={outletRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-24">
            <Outlet />
          </div>
          <MobileBottomNav />
          <WhatsAppButton bottomClassName="bottom-24" rightClassName="right-4" zClassName="z-[90]" />
        </div>
      </div>
    </DesktopRedirect>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return;

    const base = 'width=device-width, initial-scale=1.0';
    const locked = base + ', maximum-scale=1.0, minimum-scale=1.0, user-scalable=no';

    const forceZoomReset = () => {
      // Toggle minimum-scale to force iOS Safari to snap back to 100% zoom
      viewport.setAttribute('content', locked);
      setTimeout(() => {
        viewport.setAttribute('content', base + ', maximum-scale=1.0');
      }, 300);
    };

    const lock = () => viewport.setAttribute('content', locked);
    const unlock = () => {
      // Small delay so the selection registers before we restore
      setTimeout(forceZoomReset, 50);
    };

    document.addEventListener('focusin', lock, { passive: true });
    document.addEventListener('focusout', unlock, { passive: true });

    return () => {
      document.removeEventListener('focusin', lock);
      document.removeEventListener('focusout', unlock);
    };
  }, []);

  return (
    <Provider store={store}>
      <PermissionProvider>
        <Router>
          <ScrollToTop />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="w-10 h-10 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" /></div>}>
          <Routes>
            <Route element={<Layout />}>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/makes" element={<MakesPage />} />
              {/* Redirect legacy non-mobile paths to /mobile/ namespace */}
              <Route path="/makes/:makeId/models/:modelId/body-types/:bodyTypeId/positions" element={<RedirectToMobilePositions />} />
              <Route path="/makes/:makeId/models/:modelId/body-types/:bodyTypeId/positions/products" element={<RedirectToMobilePositionsProducts />} />
              {/* Vehicle flow: products for a specific position selection */}
              <Route path="/makes/:makeId/models/:modelId/series/:seriesId/body-types/:bodyTypeId/positions/:positionId/products" element={<ProductsPage />} />
              <Route path="/accessories-supplies" element={<AccessoriesSupplies />} />
              <Route path="/accessories-supplies/:categorySlug" element={<AccessoriesSupplies />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/catalogue-search" element={<CatalogueSearchPage />} />
              <Route path="/parts-finder" element={<PartsFinderPage />} />
              <Route path="/autoglass-vin-search" element={<AutoglassVinSearch />} />
              <Route path="/wish" element={<WishPage />} />
              <Route path="/watchlist" element={<WatchlistPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:productSlug" element={<ProductDetailPage />} />
              <Route path="/pages/:slug" element={<PageRenderer />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/quick-entry" element={<QuickEntryPage />} />
              
              {/* Quote Response (Public) */}
              <Route path="/quote-response/:id" element={<QuoteResponse />} />
              
              {/* Auth Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<Register1 />} />
              <Route path="/register1" element={<Register1 />} />
              
              {/* Protected Routes */}
              <Route path="/checkout" element={<ProtectedRoute requireAuth={true}><CheckoutPage /></ProtectedRoute>} />
              <Route path="/payment-test" element={<ProtectedRoute requireAuth={true}><PaymentTestPage /></ProtectedRoute>} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/orders/:orderId" element={<OrderDetailPage />} />
              <Route path="/order-confirmation/:orderId" element={<OrderConfirmationPage />} />
              <Route path="/quote-requests" element={<ProtectedRoute requireAuth={true}><QuoteRequestsPage /></ProtectedRoute>} />
              
              {/* Admin Routes (permission-based) */}
              <Route path="/admin" element={<ProtectedRoute requiredPermission="dashboard"><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/dashboard" element={<ProtectedRoute requiredPermission="dashboard"><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/products" element={<ProtectedRoute requiredPermission="products"><AdminProducts /></ProtectedRoute>} />
              <Route path="/admin/vehicle-fitments" element={<ProtectedRoute requiredPermission="products"><AdminVehicleFitments /></ProtectedRoute>} />
              <Route path="/admin/product-duplicates" element={<ProtectedRoute requiredPermission="products"><AdminProductDuplicates /></ProtectedRoute>} />
              <Route path="/admin/products/new" element={<ProtectedRoute requiredPermission="products"><AdminProductForm /></ProtectedRoute>} />
              <Route path="/admin/products/:id/edit" element={<ProtectedRoute requiredPermission="products"><AdminProductForm /></ProtectedRoute>} />
              <Route path="/admin/product-images" element={<ProtectedRoute requiredPermission="product-images"><AdminProductImages /></ProtectedRoute>} />
              <Route path="/admin/categories" element={<ProtectedRoute requiredPermission="categories"><AdminCategories /></ProtectedRoute>} />
              <Route path="/admin/product-attributes" element={<ProtectedRoute requiredPermission="product-attributes"><AdminProductAttributes /></ProtectedRoute>} />
              <Route path="/admin/makes" element={<ProtectedRoute requiredPermission="makes"><AdminMakes /></ProtectedRoute>} />
              <Route path="/admin/models" element={<ProtectedRoute requiredPermission="models"><AdminModels /></ProtectedRoute>} />

              <Route path="/admin/body-types" element={<ProtectedRoute requiredPermission="body-types"><AdminBodyTypes /></ProtectedRoute>} />
              <Route path="/admin/positions" element={<ProtectedRoute requiredPermission="positions"><AdminPositions /></ProtectedRoute>} />
              <Route path="/admin/diagram-mapper" element={<ProtectedRoute requiredPermission="diagram-workplace"><AdminDiagramMapper /></ProtectedRoute>} />
              <Route path="/admin/warehouses" element={<ProtectedRoute requiredPermission="warehouses"><AdminWarehouses /></ProtectedRoute>} />
              <Route path="/admin/stock-levels" element={<ProtectedRoute requiredPermission="stock-levels"><AdminStockLevels /></ProtectedRoute>} />
              <Route path="/admin/realtime-stock" element={<ProtectedRoute requiredPermission="stock-levels"><AdminRealtimeStock /></ProtectedRoute>} />
              <Route path="/admin/customers" element={<ProtectedRoute requiredPermission="customers"><AdminCustomers /></ProtectedRoute>} />
              <Route path="/admin/customers/:customerId" element={<ProtectedRoute requiredPermission="customers"><AdminCustomerDetail /></ProtectedRoute>} />
              <Route path="/admin/customer-prices" element={<ProtectedRoute requiredPermission="customer-prices"><AdminCustomerPrices /></ProtectedRoute>} />
              <Route path="/admin/price-categories" element={<ProtectedRoute requiredPermission="price-categories"><AdminPriceCategories /></ProtectedRoute>} />
              <Route path="/admin/product-pricing" element={<ProtectedRoute requiredPermission="product-pricing"><AdminProductPricing /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute requiredPermission="users"><AdminUsers /></ProtectedRoute>} />
              <Route path="/admin/custom-pricing" element={<ProtectedRoute requiredPermission="custom-pricing"><AdminCustomPricing /></ProtectedRoute>} />
              <Route path="/admin/orders" element={<ProtectedRoute requiredPermission="orders"><AdminOrders /></ProtectedRoute>} />
              <Route path="/admin/low-stock" element={<ProtectedRoute requiredPermission="low-stock"><AdminLowStock /></ProtectedRoute>} />
              <Route path="/admin/csv-upload" element={<ProtectedRoute requiredPermission="csv-upload"><AdminCSVUpload /></ProtectedRoute>} />
              <Route path="/admin/csv-export" element={<ProtectedRoute requiredPermission="csv-export"><AdminCsvExport /></ProtectedRoute>} />
              <Route path="/admin/csv-upload/vin-decode" element={<ProtectedRoute requiredPermission="vin-decode-csv-upload"><AdminVinDecodeCsvUpload /></ProtectedRoute>} />
              <Route path="/admin/csv-export/vin-decode" element={<ProtectedRoute requiredPermission="vin-decode-csv-export"><AdminVinDecodeCsvExport /></ProtectedRoute>} />
              <Route path="/admin/vin-decode" element={<ProtectedRoute requiredPermission="vin-decode"><AdminVinDecode /></ProtectedRoute>} />
              <Route path="/admin/identify-new-vehicle" element={<ProtectedRoute requiredPermission="identify-new-vehicle"><AdminIdentifyNewVehicle /></ProtectedRoute>} />
              <Route path="/admin/new-products-development" element={<ProtectedRoute requiredPermission="new-products-development"><AdminNewProductsDevelopment /></ProtectedRoute>} />
              <Route path="/admin/composite-coverage-gaps" element={<ProtectedRoute requiredPermission="composite-coverage-gaps"><AdminCompositeCoverageGaps /></ProtectedRoute>} />
              <Route path="/admin/coverage-gaps" element={<ProtectedRoute requiredPermission="coverage-gaps"><AdminCoverageGaps /></ProtectedRoute>} />
              <Route path="/admin/market-research" element={<ProtectedRoute requiredPermission="market-research"><AdminMarketResearch /></ProtectedRoute>} />
              <Route path="/admin/mynags-data" element={<ProtectedRoute requiredPermission="csv-upload"><AdminMynagsData /></ProtectedRoute>} />
              <Route path="/admin/mynags-research" element={<ProtectedRoute requiredPermission="mynags-research"><AdminMynagsResearch /></ProtectedRoute>} />
              <Route path="/admin/hidden-research" element={<ProtectedRoute requiredPermission="mynags-research"><AdminHiddenResearch /></ProtectedRoute>} />
              <Route path="/admin/uom" element={<ProtectedRoute requiredPermission="uom"><AdminUOM /></ProtectedRoute>} />
              <Route path="/admin/competitors" element={<ProtectedRoute requiredPermission="competitors"><AdminCompetitors /></ProtectedRoute>} />
              <Route path="/admin/product-accessories" element={<ProtectedRoute requiredPermission="product-accessories"><AdminProductAccessories /></ProtectedRoute>} />
              <Route path="/admin/common-use" element={<ProtectedRoute requiredPermission="common-use"><AdminCommonUse /></ProtectedRoute>} />
              <Route path="/admin/cms-pages" element={<ProtectedRoute requiredPermission="cms-pages"><AdminCMSPages /></ProtectedRoute>} />
              <Route path="/admin/quote-management" element={<ProtectedRoute requiredPermission="quote-management"><AdminQuoteManagement /></ProtectedRoute>} />
              <Route path="/admin/role-permissions" element={<ProtectedRoute requiredPermission="role-permissions"><AdminRolePermissions /></ProtectedRoute>} />
              <Route path="/admin/pending-users" element={<ProtectedRoute requiredPermission="users"><AdminPendingUsers /></ProtectedRoute>} />
              <Route path="/admin/settings/theme" element={<ProtectedRoute requiredPermission="settings-theme"><AdminThemeSettings /></ProtectedRoute>} />
              <Route path="/admin/settings/pricing" element={<ProtectedRoute requiredPermission="settings-pricing"><AdminPricingSettings /></ProtectedRoute>} />
              <Route path="/admin/settings/accessory-review" element={<ProtectedRoute requiredPermission="product-accessories"><AdminAccessoryReview /></ProtectedRoute>} />
              <Route path="/admin/settings/competitor-codes-review" element={<ProtectedRoute requiredPermission="role-permissions"><AdminCompetitorCodesReview /></ProtectedRoute>} />
              <Route path="/admin/settings/warehouse-emails" element={<ProtectedRoute requiredPermission="settings-warehouse-emails"><AdminWarehouseEmailSettings /></ProtectedRoute>} />
              <Route path="/admin/batch-vin-tester" element={<ProtectedRoute requiredPermission="batch-vin-tester"><BatchVinTester /></ProtectedRoute>} />
              <Route path="/admin/ip-blocklist" element={<ProtectedRoute requiredPermission="dashboard"><AdminIPBlocklist /></ProtectedRoute>} />
              <Route path="/admin/ip-blocks" element={<ProtectedRoute requiredPermission="dashboard"><AdminIPBlocks /></ProtectedRoute>} />
              <Route path="/admin/csv-combiner" element={<ProtectedRoute requiredPermission="csv-combiner"><AdminCSVCombiner /></ProtectedRoute>} />
              <Route path="/vin-search" element={<VinSearch />} />
              
              {/* SEO landing pages — must be before :makeSlug catch-all */}
              <Route path="/windscreen-replacement-sydney" element={<WindscreenReplacementSydney />} />
              <Route path="/car-glass-repair-sydney" element={<CarGlassRepairSydney />} />
              <Route path="/auto-glass-services-nsw" element={<AutoGlassServicesNSW />} />
              <Route path="/windscreen-replacement-melbourne" element={<WindscreenReplacementMelbourne />} />
              <Route path="/auto-glass-services-vic" element={<AutoGlassServicesVIC />} />

              {/* Short SEO URLs — must come last to avoid shadowing static routes */}
              <Route path="/:makeSlug/:modelSlug/:productSlug" element={<ProductDetailPage />} />
              <Route path="/:makeSlug" element={<EnhancedModelsPage />} />
              <Route path="/:makeSlug/:modelSlug" element={<BodyTypesRouter />} />
              {/* 404 Route for desktop namespace */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Standalone mobile namespace */}
            <Route path="/mobile" element={<MobileShell />}>
              <Route index element={<MobileHomePage />} />
              <Route path="makes" element={<MobileMakesPage />} />
              <Route path="models" element={<MobileModelsPage />} />
              {/* New flow: Make -> Model -> Year -> Body Type -> Positions -> Products */}
              <Route path="makes/:makeId/models/:modelId/year" element={<MobileYearPage />} />
              <Route path="makes/:makeId/models/:modelId/year/:year/body-types" element={<MobileBodyTypesPage />} />
              <Route path="makes/:makeId/models/:modelId/year/:year/body-types/:bodyTypeId/positions" element={<MobilePositionsPage />} />
              <Route path="makes/:makeId/models/:modelId/year/:year/body-types/:bodyTypeId/positions/products" element={<MobileProductsPage />} />
              {/* Legacy route redirects (old flow without year) */}
              <Route path="makes/:makeId/models/:modelId/body-types" element={<MobileBodyTypesPage />} />
              <Route path="makes/:makeId/models/:modelId/body-types/:bodyTypeId/positions" element={<MobilePositionsPage />} />
              <Route path="makes/:makeId/models/:modelId/body-types/:bodyTypeId/positions/products" element={<MobileProductsPage />} />
              <Route path="products/:productSlug" element={<MobileProductDetailPage />} />
              <Route path="cart" element={<MobileCartPage />} />
              <Route path="checkout" element={<ProtectedRoute requireAuth={true}><MobileCheckoutPage /></ProtectedRoute>} />
              <Route path="parts-finder" element={<MobilePartsFinderPage />} />
              <Route path="accessories" element={<MobileAccessoriesPage />} />
              <Route path="accessories-supplies" element={<MobileAccessoriesPage />} />
              <Route path="quick-entry" element={<MobileQuickEntryPage />} />
              <Route path="accounts" element={<MobileAccountsPage />} />
              <Route path="watchlist" element={<MobileWatchlistPage />} />
              <Route path="login" element={<MobileLoginPage />} />
              <Route path="register" element={<Register1 />} />
              <Route path="contact" element={<MobileContactPage />} />
              <Route path="vin-search" element={<MobileVinSearchPage />} />
              <Route path="search" element={<MobileSearchResultsPage />} />
              <Route path="products/:productSlug/request-quote" element={<MobileQuoteRequestPage />} />
              <Route path="pages/:slug" element={<MobilePageRenderer />} />
              {/* Slug-based model/make URLs shared via desktop links — redirect to desktop resolver */}
              <Route path=":makeSlug/:modelSlug/:productSlug" element={<MobileSlugModelRedirect />} />
              <Route path=":makeSlug/:modelSlug" element={<MobileSlugModelRedirect />} />
              <Route path=":makeSlug" element={<MobileSlugModelRedirect />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                theme: {
                  primary: '#4aed88',
                },
              },
            }}
          />
          <AppUpdateBanner />
        </Router>
      </PermissionProvider>
    </Provider>
  );
}

export default App;
